import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { getCache, setCache } from "@/lib/cache";
import { brl } from "@/lib/format";
import { STATUS } from "@/lib/status";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarClock, AlertTriangle, ChevronRight, Loader2, CalendarCheck, ArrowLeft, Search } from "lucide-react";
import { motion } from "framer-motion";

const FORMAS = { dinheiro: "Dinheiro", pix: "Pix", cartao: "Cartão", transferencia: "Transferência", outro: "Outro" };

export default function Vencimentos() {
  const [data, setData] = useState(() => getCache("venc-parcelas") ?? null);
  const [recebidos, setRecebidos] = useState(null);
  const [qVenc, setQVenc] = useState("");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const f = params.get("f") || "";

  useEffect(() => {
    setRecebidos(null);
    if (f === "recebidos") {
      setData(null);
      api.get("/dashboard/recebimentos-hoje").then((r) => setRecebidos(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    } else {
      api.get("/dashboard/parcelas").then((r) => { setData(r.data); setCache("venc-parcelas", r.data); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    }
  }, [f]);

  const titles = { "": "Vencimentos", hoje: "A receber hoje", vencidas: "Prestações atrasadas", recebidos: "Recebido no dia" };
  const subs = { "": "Prestações separadas por data de vencimento.", hoje: "Prestações que vencem hoje.", vencidas: "Prestações em atraso.", recebidos: "Pagamentos recebidos hoje." };

  const label = (g) => {
    const d = new Date(g.date + "T00:00:00");
    const txt = d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
    if (g.is_today) return `Hoje · ${txt}`;
    if (g.is_overdue) return `Atrasado · ${txt}`;
    return txt;
  };

  const header = (
    <>
      {f && <button onClick={() => navigate("/painel")} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-3 transition-colors" data-testid="venc-voltar"><ArrowLeft className="w-4 h-4" /> Painel</button>}
      <h1 className="font-display text-3xl sm:text-4xl font-black text-foreground mb-1">{titles[f] || "Vencimentos"}</h1>
      <p className="text-muted-foreground mb-6">{subs[f] || ""}</p>
    </>
  );

  if (f === "recebidos") {
    if (!recebidos) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div></AdminLayout>;
    return (
      <AdminLayout>
        {header}
        <div className="rounded-2xl border border-green-500/30 bg-green-500/10 p-5 mb-6" data-testid="recebido-total-card">
          <div className="flex items-center gap-2 text-green-400 text-sm font-medium"><CalendarCheck className="w-4 h-4" /> Total recebido hoje</div>
          <p className="font-display text-4xl font-black text-green-400 mt-1">{brl(recebidos.total)}</p>
        </div>
        {recebidos.items.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-12 text-center"><p className="text-muted-foreground">Nenhum pagamento recebido hoje.</p></div>
        ) : (
          <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="recebidos-list">
            {recebidos.items.map((it, i) => (
              <button key={i} onClick={() => navigate(`/painel/ficha/${it.ficha_id}`)}
                className={`w-full flex items-center justify-between px-5 py-4 text-left hover:bg-secondary transition-colors ${i !== recebidos.items.length - 1 ? "border-b border-border" : ""}`}
                data-testid={`recebido-item-${i}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-foreground truncate">#{it.numero} · {it.cliente_nome}</p>
                  <p className="text-sm text-muted-foreground truncate">Parcela {it.parcela ?? "—"} · {FORMAS[it.forma] || it.forma}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <p className="font-display font-bold text-green-400">{brl(it.valor)}</p>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
        )}
      </AdminLayout>
    );
  }

  if (!data) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div></AdminLayout>;

  let groups = data.groups;
  if (f === "hoje") groups = groups.filter((g) => g.is_today);
  const allOverdue = data.overdue_clientes || [];
  const overdueClientes = qVenc ? allOverdue.filter((c) => (c.nome || "").toLowerCase().includes(qVenc.toLowerCase())) : allOverdue;
  const emptyVencidas = f === "vencidas" && allOverdue.length === 0;

  return (
    <AdminLayout>
      {header}
      {!f && (
        <div className="grid grid-cols-2 gap-4 mb-8">
          <button onClick={() => navigate("/painel/vencimentos?f=hoje")} className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-5 text-left hover:border-yellow-500/60 transition-colors" data-testid="due-today-card">
            <div className="flex items-center gap-2 text-yellow-400 text-sm font-medium"><CalendarClock className="w-4 h-4" /> Vencem hoje</div>
            <p className="font-display text-4xl font-black text-yellow-400 mt-1">{data.vencem_hoje}</p>
          </button>
          <button onClick={() => navigate("/painel/vencimentos?f=vencidas")} className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5 text-left hover:border-red-500/60 transition-colors" data-testid="overdue-card">
            <div className="flex items-center gap-2 text-red-400 text-sm font-medium"><AlertTriangle className="w-4 h-4" /> Atrasadas</div>
            <p className="font-display text-4xl font-black text-red-400 mt-1">{data.vencidas}</p>
          </button>
        </div>
      )}

      {f === "vencidas" ? (
        emptyVencidas ? (
          <div className="rounded-2xl border border-border bg-card p-12 text-center">
            <CalendarCheck className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-muted-foreground">Nenhum cliente em atraso. 🎉</p>
          </div>
        ) : (
          <>
          <div className="relative mb-4">
            <Search className="w-5 h-5 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
            <Input data-testid="search-vencidas" value={qVenc} onChange={(e) => setQVenc(e.target.value)}
              placeholder="Buscar cliente pelo nome..." className="pl-12 rounded-full bg-card" style={{ height: 52 }} />
          </div>
          {overdueClientes.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card p-10 text-center">
              <p className="text-muted-foreground">Nenhum cliente encontrado para "{qVenc}".</p>
            </div>
          ) : (
          <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="overdue-clientes-list">
            {overdueClientes.map((c, i) => (
              <button key={c.key} onClick={() => navigate(`/painel/cliente/${encodeURIComponent(c.key)}`)}
                className={`w-full flex items-center justify-between px-5 py-4 text-left hover:bg-secondary transition-colors ${i !== overdueClientes.length - 1 ? "border-b border-border" : ""}`}
                data-testid={`overdue-cliente-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0 bg-red-500" />
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground truncate">{c.nome}</p>
                    <p className="text-sm text-red-400 truncate">{c.qtd_parcelas} parcela{c.qtd_parcelas > 1 ? "s" : ""} em atraso{c.dias_atraso > 0 ? ` · ${c.dias_atraso} dias` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <p className="font-display font-bold text-red-400">{brl(c.saldo)}</p>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
          )}
          </>
        )
      ) : (
      <>
      {groups.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-12 text-center">
          <CalendarCheck className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-muted-foreground">Nenhuma parcela {f === "hoje" ? "vencendo hoje" : "em aberto com vencimento"}.</p>
        </div>
      )}

      <div className="space-y-6">
        {groups.map((g) => (
          <motion.section key={g.date} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid={`due-group-${g.date}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-2.5 h-2.5 rounded-full ${g.is_overdue ? "bg-red-500" : g.is_today ? "bg-yellow-500" : "bg-slate-500"}`} />
              <h2 className={`font-display font-bold capitalize ${g.is_overdue ? "text-red-400" : g.is_today ? "text-yellow-400" : "text-muted-foreground"}`}>{label(g)}</h2>
            </div>
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
              {g.items.map((it, i) => {
                const st = STATUS[it.status] || STATUS.pendente;
                return (
                  <button key={`${it.ficha_id}-${it.parcela}`} onClick={() => navigate(`/painel/ficha/${it.ficha_id}`)}
                    className={`w-full flex items-center justify-between px-5 py-4 text-left hover:bg-secondary transition-colors ${i !== g.items.length - 1 ? "border-b border-border" : ""}`}
                    data-testid={`due-item-${it.ficha_id}-${it.parcela}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground truncate">#{it.numero} · {it.cliente_nome}</p>
                      <p className="text-sm text-muted-foreground truncate">Parcela {it.parcela} · {it.remarcacoes > 0
                        ? <span className="text-orange-400 font-medium">Remarcada {it.remarcacoes}x · vence {new Date(it.vencimento + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}</span>
                        : <span className={st.text}>{st.label}</span>}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <p className="font-display font-bold text-red-400">{brl(it.saldo)}</p>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.section>
        ))}
      </div>
      </>
      )}
    </AdminLayout>
  );
}
